// External dependencies
import moment from 'moment';
import cloneDeep from 'lodash.clonedeep';

// Internal dependencies
import { spanishBenchmarksOverTime, benchmarksOverTime } from '../../../dummy_data/reports/benchmarksOverTime';
import { locales } from "../../../constants/constants";
import { getCutscorePercentiles } from './cutscores';
import { SCALE_CONSTANTS, getPercentileValue } from '../functions/getPR';
import { getSchoolYear } from './validation';

function getDistrictBenchmarks(benchmark, type, customBenchmarks) {
  let districtCutlines = [];

  // if invalid district cutline data, return two empty arrays
  if(!customBenchmarks || !customBenchmarks.district || !customBenchmarks.district.cutlines) {
    districtCutlines.push([]);
    districtCutlines.push([]);
    return districtCutlines;
  }

  // else generate the cutlines
  let grade = benchmark.grade;
  let value = benchmark.value;
  // TODO extend to accomodate three cutlines
  for(let i = 0; i < customBenchmarks.district.cutlines.length; i++) {
    let cutline = customBenchmarks.district.cutlines[i];
    let scores = generateDistrictCutlineOverTime(cutline, grade);
    districtCutlines.push(scores);
  }
  return districtCutlines;
}

/* generate a score for each day of the year,
   interpolating between each season:
   Aug to December = Fall (153 days)
   Jan to March = Winter (91 days)
   April to July = Spring (121 days + 1 in case of leap year)
*/
function generateDistrictCutlineOverTime(cutline, grade) {
  let fields = cutline.grades[grade].seasons;
  let scores = [];
  let daysInSeason = [153, 93, 122];
  for(let j = 0; j < daysInSeason.length; j++) {
    let days = daysInSeason[j];
    for(let k = 0; k < days; k++) {
      let cutlineAtNextSeason;
      if(j < 2) {
        cutlineAtNextSeason = fields[j + 1];
      } else {
        if(grade < 3) {
          // Look up fall cutline for the next grade
          cutlineAtNextSeason = cutline.grades[grade + 1].seasons[0];
        } else {
          // Known bug: we currently don't project beyond
          // the third grade spring cutpoint
          cutlineAtNextSeason = fields[j];
        }
      }
      // Note: we depend on cutlines being ordinal (monotonically increasing)
      let difference = cutlineAtNextSeason.value - fields[j].value;
      let score = fields[j].value + ((k/days) * difference);
      let roundedScore = Math.round(score * 100) / 100;
      scores.push(roundedScore);
    }
  }
  return scores;
}

const cutlineWindows = ["BOY", "MOY", "EOY"];

// the location of BOY, MOY, and EOY in the cutline array generated by getNationalNormBenchmarks
function getCutlineArrayIndices() {
  const cutlineArrayIndices = [60, 182, 302];
  const schoolYear = getSchoolYear();
  const endDate = schoolYear?.endDate;
  const endYear = endDate?.getFullYear();
  // add an extra day for Feb 29th in leap years
  if (isLeapYear(endYear)) cutlineArrayIndices[2] += 1;
  return cutlineArrayIndices;
}

function isLeapYear(year) {
  return (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0));
}

function getNationalNormBenchmarks(benchmark, scale, selectedStatePercentileCuts) {
  const cutlineArrayIndices = getCutlineArrayIndices();
  const grade = benchmark.grade;
  const language = benchmark.value.includes("Spanish") ? locales.ES_MX : locales.EN_US;
  const schoolYear = getSchoolYear();
  const endDate = schoolYear?.endDate;
  const endYear = endDate?.getFullYear();
  if (SCALE_CONSTANTS[scale]) {
    const percentiles = getCutscorePercentiles(selectedStatePercentileCuts);
    return percentiles.map((perc) => {
      const cutline = [];
      for (let i = 0; i < cutlineWindows.length; i++) {
        const window = cutlineWindows[i];
        const index = cutlineArrayIndices[i];
        let value = getPercentileValue({
          percentile: perc,
          scale,
          grade,
          language,
          window,
        })
       // interpolate BOY
       if (window === 'MOY') {
          const moyIndex = cutlineArrayIndices[1];
          const boyIndex = cutlineArrayIndices[0];
          const daysInWindow = moyIndex - boyIndex;
          const offsetPerDay = (value - cutline[boyIndex]) / daysInWindow;
          cutline[0] = cutline[boyIndex] - (offsetPerDay * boyIndex);
        }
        if (window === 'EOY') {
          const eoyIndex = cutlineArrayIndices[2];
          const moyIndex = cutlineArrayIndices[1];
          const daysInWindow = eoyIndex - moyIndex;
          const offsetPerDay = (value - cutline[moyIndex]) / daysInWindow;
          const lastIndex = isLeapYear(endYear) ? 363 : 362;
          cutline[lastIndex] = cutline[moyIndex] + (offsetPerDay * (lastIndex - moyIndex));
          cutline[364] = value; // the value at index 364 is used for extended school years
        }
        cutline[index] = value;
      }
      return cutline;
    });
  } else {
    // fallback to legacy method of cutline calculation
    const percentiles = [25, 50, 75];
    return percentiles.map((perc) => {
      const values = language === locales.EN_US ? benchmarksOverTime : spanishBenchmarksOverTime;
      const rawBench = values.find(b => b.grade === grade && b.PR === perc && b.scoreType == scale);
      return rawBench?.score || [];
    })
  }
}

// Adds a date field to each score, starting with August 15th and incrementing a day per score
// TODO refactor to take a daterange when removing hardcoded benchmarks
function mapScoresWithDates(scores, startDate, endDate, lastYearCutline, startYear) {

  // intialize vars
  let scoresWithDates = [];
  let currDate = moment(startDate)
  let benchmarkDataBeginDate = moment("08-01-" + startYear, "MM-DD-YYYY"); // date on which benchmark data begins

  // for each day between startDate and endDate
  while(currDate.isBefore(moment(endDate).subtract(1, 'days'))) {
    // calculate which benchmark score is appropriate for the date
    let dayOffset = currDate.diff(benchmarkDataBeginDate, 'days'); // offset into benchmark data array
    let useLastYearCutline = false;
    if(Math.sign(dayOffset) == -1) { // if negative, use a benchmark from last year
      dayOffset = 365 + dayOffset;
      useLastYearCutline = true;
    }
    let offset = Math.min(dayOffset,364);
    const date = currDate.clone();

    // if such a score exists, push it to the return array with the corresponding date
    if(useLastYearCutline) {
      if(lastYearCutline) {
        if (lastYearCutline[offset] !== undefined) {
          scoresWithDates.push([date.valueOf(), lastYearCutline[offset]]);
        }
      }
    } else {
      if (scores[offset] !== undefined) {
        scoresWithDates.push([date.valueOf(), scores[offset]]);
      }
    }
    currDate.add(1, 'days'); // increment currDay
  }
  return scoresWithDates;
}

// returns district or national norm benchmarks
function getBenchmarksByCategory(benchmark, type, customBenchmarks, category, selectedStatePercentileCuts) {
  let cutlines;

  switch(category) { // TODO add a case here for custom benchmark
    case 'District':
      cutlines = getDistrictBenchmarks(benchmark, type, customBenchmarks);
      break;
    case 'Spanish':
      cutlines = getNationalNormBenchmarks(benchmark,type, selectedStatePercentileCuts);
      break;
    case 'NationalNorms':
    default:
      cutlines = getNationalNormBenchmarks(benchmark, type, selectedStatePercentileCuts);
      break;
  }

  return cutlines;
}

// Returns benchmarks for each day, from August 15th to August 14th
export function getScaledBenchmarkCutlinesOverTime(benchmark, type, customBenchmarks, startDate, endDate, isLegacyCustomer, selectedStatePercentileCuts) {
  let benchmarkCategory = benchmark.value.substring(0, benchmark.value.length-2);
  if(benchmarkCategory != 'District' && benchmarkCategory != 'NationalNorms' && benchmarkCategory != 'Spanish') {
    return {lowCutline: [], midCutline: [], highCutline: []};
  }

  // generate cutlines
  let cutlines = getBenchmarksByCategory(benchmark, type, customBenchmarks, benchmarkCategory, selectedStatePercentileCuts);

  // If this is a legacy customer, we'll need last year's cutlines too
  // TODO deprecate this
  /*
    NOTE:
    This codeblock prevents our legacy customer's activities
    from disappearing from the progress chart when the school year updates (due to the
    range of the chart shifting to the next school year).

    Students currently don't automatically "graduate" in SIS, and we're back-projecting benchmarks
    under the assumption that the SIS grade refers to the current year. For this reason, in
    order to support students who are marked as kindergarten, but who completed activity last
    year, we're assuming that they graduated to first grade.
  */
  let lastYearCutlines = null;
  if(isLegacyCustomer) {
    if(benchmark.grade > 0) {
      let lastYearBenchmark = cloneDeep(benchmark);
      lastYearBenchmark.grade = benchmark.grade - 1;
      lastYearCutlines = getBenchmarksByCategory(lastYearBenchmark, type, customBenchmarks, benchmarkCategory);
    } else { // If legacy kinder, automatically bump them to first grade
      lastYearCutlines = getBenchmarksByCategory(benchmark, type, customBenchmarks, benchmarkCategory);
      let thisYearBenchmark = cloneDeep(benchmark);
      thisYearBenchmark.grade = benchmark.grade + 1;
      cutlines = getBenchmarksByCategory(thisYearBenchmark, type, customBenchmarks, benchmarkCategory);
    }
  }

  // Parse out high and low cutlines for last year, if they exist
  let lastYearLowCutline = null;
  let lastYearHighCutline = null;
  if(lastYearCutlines) {
    lastYearLowCutline = lastYearCutlines[0];
    lastYearHighCutline = lastYearCutlines[1];
  }

  // Map the scores to dates
  const startYear = moment(startDate).year();
  let lowCutline = mapScoresWithDates(cutlines[0], startDate, endDate, lastYearLowCutline, startYear);
  let midCutline = mapScoresWithDates(cutlines[1], startDate, endDate, lastYearHighCutline, startYear);
  let highCutline = mapScoresWithDates(cutlines[2], startDate, endDate, lastYearHighCutline, startYear);

  return { lowCutline, midCutline, highCutline };
}

export default {
  getScaledBenchmarkCutlinesOverTime,
}